interface IQueryInfo {
    query: string;
    idMember?: string;
    orderBy?: string;
    template?: string;
    refreshTime: number;
    disableDoubleClickOnSelect: boolean;

    filter?: IQueryRequestFilter;
    filterView?: string;
}
interface IQueryMetaData {
    info: {
        selectAction?: IActionInfo;
        selectMultiAction?: IActionInfo;

        filter: any;
        orderBy: any;
    }    
}
type SiteElementSearchMode = 'normal' | 'multiselect' | 'edit';

class SiteElementSearch
    extends QueryElement<HTMLElement> {

    private _eventRefresh: (e: CustomEvent) => any;
    private _eventContainerInactive: (e: Event) => any;
    private _eventContainerActive: (e: Event) => any;
    private _eventResize: (data: CustomEvent) => any;

    private _mode: SiteElementSearchMode = 'normal';
    private _multiSelection: ISiteHelperQueryRequestItem[] = [];

    private readonly _changedEvent: (data: ISiteModelValueChanged) => void;
    private readonly _statisticsEvent: (data: IQueryResponseStatistics) => Promise<void>;
    private readonly _clickEvent: (data: QueryEventedModel<ISiteHelperQueryRequestItem>) => Promise<void>;
    private readonly _templateEvent: (data: ISiteHelperQueryTemplate) => Promise<void>;

    private readonly _selectButton: SiteButton;
    private readonly _editButton: SiteButton;
    private readonly _saveButton: SiteButton;

    private readonly _results: ISiteHelperQuery;
    private readonly _card: SiteCard;
    private readonly _query: IQueryInfo;
    private readonly _queryMetadata: IQueryMetaData;
    private readonly _innerModel: SiteModel;

    private readonly _filterPath: string | null;
    private readonly _modelPath: string | null;

    constructor(card: SiteCard, elem: HTMLElement) {
        super(elem);

        this._card = card;
        let siteConfig = card.SiteConfig;

        // Get info
        let path = this.getAttr("data-path");

        this._query = path && this._card.Model.get(SiteModel.split(path));
        this._queryMetadata = path && this._card.ModelData.get(SiteModel.split(path));

        // Add filters
        this._innerModel = new SiteModel({
            filter: this._query.filter,
            orderBy: this._query.orderBy,
            template: this._query.template
        });
        let modelData = new SiteModel({
            filter: this._queryMetadata.info.filter,
            orderBy: this._queryMetadata.info.orderBy
        });

        // Get elements
        let results = this.query(".results").create((a : HTMLElement) =>
            new SiteHelperQuery2d(a, this._query, {
                perPage: 25,
                extraBottom: 5,
                extraTop: 5,
                maxRefresh: this._query.refreshTime || 120,
                autoRefresh: true
            })
        );
        let info = this.query(".header > label > .info").first();
        let fieldSet = this.query(".header > .fieldset").create<SiteHelperCollapsable, HTMLElement>(a => new SiteHelperCollapsable(a));
        let label = this.query(".header > label").create<SiteLabelInput, HTMLElement>(a => {
            a.setAttribute("data-path", "filter.input");
            let inputLabel = new SiteLabelInput(card, this._innerModel, new SiteModel({}), a);
            inputLabel.setConfig({
                setValueTimeout: 250,
                popout: "none",
                scroll: "top"
            });
            inputLabel.getInput().on("keydown", (e: KeyboardEvent) => {
                switch (e.keyCode) {
                    case 13: // ENTER
                        let selector = this._results.getSelector();
                        selector && this.click(selector);
                        break;    
                    case 38: // KEY UP
                        this._results.selectorUp();    
                        break;
                    case 40: // KEY DOWN
                        this._results.selectorDown();    
                        break;
                    case 17:

                }
            });
            inputLabel.init(inputLabel.getInput());

            return inputLabel;
        });
        let labelString = this.query(".header > label > .label").first();
        
        if (results === null)
            throw "Missing required elements!";
        
        // Save some
        this._results = results;
        this._modelPath = this.getAttr("data-model-path");
        this._filterPath = this.getAttr("data-filter-path");
        
        // Add search...
        labelString && labelString.setHtml(siteConfig.strings["search..."]);

        // Lock events
        this._changedEvent = this._innerModel.changedEvent.on(async a => {
            if (a.path[0] == "filter")
                await this._results.setFilter(this._innerModel.get(["filter"]));
            else if (a.path[0] == "orderBy")
                await this._results.setOrderBy(this._innerModel.get(["orderBy"]));
            else if (a.path[0] == "template")
                await this._results.setTemplate(this._innerModel.get(["template"]));
        });
        
        this._templateEvent = this._results.templateEvent.on(async a => {
            if (a.type == "grid")
                this._card.setSize("Large");
            else
                this._card.setSize();
        })
        this._clickEvent = this._results.clickEvent.on(async a => { await this.click(a.model); });
        this._statisticsEvent = this._results.statisticsEvent.on(async a => {
            info && info.setHtml(`${a.totalResults} ${siteConfig.strings["results"]}`);
        });

        // --- Create container
        let container = QueryElement.QueryElement("div")
            .addClass("content");
        
        // -- Create sort / Template
        let sortTemplate = SiteLabelDropdown.getTemplate("{name}", "<dl><dt>{name}</dt></dl>", siteConfig.strings["sort"], "orderBy", "DirectCode.Module.Base.Queries.QueryOrderListQuery`1[[" + this._query.query + "]], DirectCode.Module.Base").get();
        let templateTemplate = SiteLabelDropdown.getTemplate("{name}", "<dl><dt>{name}</dt></dl>", siteConfig.strings["template"], "template", "DirectCode.Module.Base.Queries.QueryTemplateListQuery`1[[" + this._query.query + "]], DirectCode.Module.Base").get();

        let sortFilter = sortTemplate && new SiteLabelDropdown(card, this._innerModel, modelData, sortTemplate);
        let templateFilter = templateTemplate && new SiteLabelDropdown(card, this._innerModel, modelData, templateTemplate);

        // -- Create buttons
        let filter = new SiteButton({ title: siteConfig.strings["filter"], icon: "filter_list" }, true);
        if (!this._query.filterView) {
            filter.disable();
        } else {
            filter.on("click", async a => {
                let view = await SiteContainer.getCardView(`View/QueryFilter/${this._query.filterView}`);
                let newCard = new SiteCard(card.Container, view, {
                    model: new SiteModelInner(this._innerModel, ["filter"]),
                    observers: [],
                    metaData: new SiteModelInner(modelData, ["filter"]),
                    type: "Card",
                    options: {
                        size: "Normal",
                        cardItems: []
                    },
                    view: ""
                })

                card.Container.removeNextCards(card);
                await card.Container.appendCard(newCard);
                await card.Container.makeCardActive(newCard);
            });
        }

        this._saveButton = new SiteButton({ title: siteConfig.strings["save"], icon: "check" }, true);
        this._saveButton.on("click", async a => {
            let request: IActionTypeSubmit = {
                type: "Request",
                actionLink: {
                    actionInfo: 
                    {
                        controller: "Api",
                        action: "QuerySave"
                    },
                    model: {
                        query: this._query.query,
                        Template: this._innerModel.get(["template"]),
                        orderBy: this._innerModel.get(["orderBy"])
                    }
                },
                setModel: false
            }

            if (this._modelPath) {
                request.type = "Submit";
                request.path = this._modelPath;
            }

            await this._card.request(request);
        });

        this._editButton = new SiteButton({ title: siteConfig.strings["edit"], icon: "edit" }, true);
        this._editButton.on("click", async a => {
            if (this._results.getMode() == "edit")
            {
                this._editButton.setIcon("edit");
                this._results.setMode("view");
            }
            else
            {
                this._editButton.setIcon("edit_off");
                this._results.setMode("edit");
            }
        });

        this._selectButton = new SiteButton({ title: siteConfig.strings["select all"], icon: "select_all" }, true);
        // Set the select button
        if (this._queryMetadata.info.selectMultiAction == null) {
            this._selectButton.disable();
        } else {
            var clickHandler = new ClickHandler()
                .click(async a => {
                    if (this._selectButton.containsClass("inactive")) {
                        this.setMode('normal');
                    } else {
                        this.setMode('multiselect');
                    }
                })
                .dblclick(async a => {
                    if (!this._query.disableDoubleClickOnSelect)
                        await this.selectAll();
                })
            this._selectButton.on("click", async (a : Event) => clickHandler.call(a));
        }

        let buttonContainer = QueryElement.QueryElement("div")
            .addClass("buttons")
            .append(filter)
            .append(this._saveButton)
            .append(this._editButton)
            .append(this._selectButton);
        
        // -- Append it all
        if (sortFilter && templateFilter && fieldSet) {
            fieldSet.append(
                container
                    .append(sortFilter)
                    .append(templateFilter)
                    .append(buttonContainer)
                );
            sortFilter.init(sortFilter);
            sortFilter.renderModel({name:this._innerModel.get(["orderBy"])});
            templateFilter.init(templateFilter);
            console.log(this._innerModel);
            templateFilter.renderModel({name:this._innerModel.get(["template"])});
        }

        // Add events
        let cardStarted = card.on("qts.started", (e: CustomEvent) => {
            this._results.start();
            card.off("qts.started", cardStarted);
        });
        this._eventResize = card.on("qts.resize", (e: CustomEvent) => {
            this._results.resize();
        })
        this._eventRefresh = card.on("qts.refresh", async (e: CustomEvent) => {
            if(e.detail instanceof CancellableEvent)
                e.detail.cancelled = true;
            
            await this._results.refresh();
        });
        this._eventContainerActive = this._card.Container.on("qts.active", () => {
            this._results.start();
        });
        this._eventContainerInactive = this._card.Container.on("qts.inactive", () => {
            this._results.stop();
        });
    }
    async selectAll() {
        if (this._mode != 'multiselect')
            return;
        
        for(let item of this._results.getPageItems())
        {
            this.click(item);
        }
    }
    async setMode(mode: SiteElementSearchMode) {
        this._selectButton.removeClass("inactive");
        this._saveButton.removeClass("inactive");

        if (this._mode == 'multiselect' && mode != 'multiselect') {
            // Make all selected records unselected
            let ids = [];
            for(let item of this._multiSelection)
            {
                if (item !== undefined) {
                    ids.push(item.getResult().id);
                    item.makeUnselect();
                }    
            }
            this._multiSelection = [];

            // Submit it:
            if (this._queryMetadata.info.selectMultiAction) {
                let request: IActionTypeSubmit = {
                    type: "Request",
                    actionLink: {
                        actionInfo: this._queryMetadata.info.selectMultiAction,
                    },
                    setModel: false
                }

                if (this._modelPath) {
                    let model = this._card.Model.get(SiteModel.split(this._modelPath));
                    request.actionLink.model = {
                        "$type": `DirectCode.Modules.MVC.Models.MultiModel\`1[[${model["$type"]}]], DirectCode.Session.MVC`,
                        ids: ids,
                        model: model
                    }
                } else {
                    request.actionLink.model = {
                        "$type": `DirectCode.Modules.MVC.Models.MultiModel, DirectCode.Session.MVC`,
                        ids: ids
                    }
                }

                await this._card.request(request);
            }    

        }
        if (this._mode == 'edit' && mode != 'edit') {
            // Make resultset normal again
        }

        if (mode == 'multiselect') {
            this._selectButton.addClass("inactive");
        }
        if (mode == 'edit') {
            this._saveButton.addClass("inactive");
        }

        this._mode = mode;
    }

    async click(ev: ISiteHelperQueryRequestItem) {
        // Remove all active class
        this._results.query(".active").all<ISiteHelperQueryRequestItem, any>(a => a.makeInactive());
        this._results.query(".selector").all<ISiteHelperQueryRequestItem, any>(a => a.makeUnselector());
        
        this._results.makeSelector(ev);
        
        // Request new card
        if (this._mode == 'normal') {
            if (this._queryMetadata.info.selectAction) {
                ev.makeActive();

                this._queryMetadata.info.selectAction.id = ev.getResult().id;

                let request: IActionTypeSubmit = {
                    type: "Request",
                    actionLink: {
                        actionInfo: this._queryMetadata.info.selectAction
                    },
                    setModel: true
                }

                if (this._modelPath) {
                    request.type = "Submit";
                    request.path = this._modelPath;
                }

                await this._card.request(request);
            }
        }

        if (this._mode == 'multiselect') {
            if (ev.isSelected()) {
                ev.makeUnselect();
                delete this._multiSelection[this._multiSelection.indexOf(ev)];
            } else {
                ev.makeSelect();
                this._multiSelection.push(ev);
            }
        }
    }

    async remove(removeInner = true) {
        await super.remove(removeInner);
        this._card.off("qts.refresh", this._eventRefresh);
        this._card.off("qts.resize", this._eventResize);
        this._card.Container.off("qts.active", this._eventContainerActive);
        this._card.Container.off("qts.inactive", this._eventContainerInactive);
        this._results.clickEvent.off(this._clickEvent);
        this._results.statisticsEvent.off(this._statisticsEvent);
        this._results.templateEvent.off(this._templateEvent);
        this._innerModel.changedEvent.off(this._changedEvent);
    }
}